/**
 * Quick Add Drawer Controller
 * Features:
 *  - AJAX product fetch via Section Rendering API
 *  - Metal picker with validation (red border highlight)
 *  - Size validation
 *  - AJAX Add to Cart (no redirect) with cart toast notification
 *  - Wishlist toggle (localStorage)
 *  - Esc key + overlay click to close
 */

(function() {
  const drawer   = document.getElementById('quickAddDrawer');
  const overlay  = document.getElementById('quickAddOverlay');
  const content  = document.getElementById('quickAddContent');
  const closeBtn = document.getElementById('quickAddClose');
  const toast    = document.getElementById('cartToast');
  const WISHLIST_KEY = 'unplated_wishlist';

  if (!drawer || !content || !overlay) return;

  // ── OPEN ──────────────────────────────────────────────────────────────────
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.p-add-v2');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    const handle = btn.dataset.handle;
    if (!handle) return;
    openQuickAdd(handle);
  });

  async function openQuickAdd(handle) {
    overlay.classList.add('active');
    drawer.classList.add('active');
    document.body.style.overflow = 'hidden';
    content.innerHTML = `
      <div class="q-add-loading">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" width="28" height="28" style="animation: spin 1s linear infinite; opacity:0.4;">
          <path stroke-linecap="round" d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
        </svg>
        Preparing your selection&hellip;
      </div>`;

    try {
      const res = await fetch(`/products/${handle}?section_id=quick-add-drawer`);
      if (!res.ok) throw new Error('Failed to load');
      content.innerHTML = await res.text();
      initQuickAddForm(handle);
      // ── BYK MODE: relabel submit button ──────────────────────────
      if (typeof window._bykPickerCallback === 'function') {
        const submitBtn = content.querySelector('[type="submit"]');
        if (submitBtn && submitBtn.textContent.trim() !== 'Sold out') {
          submitBtn.textContent = 'Add to box';
        }
      }
    } catch (err) {
      content.innerHTML = `<div class="q-add-error">Sorry, we couldn't load this item.<br><small>Please try again.</small></div>`;
      console.error('[QuickAdd]', err);
    }
  }

  // ── CLOSE ─────────────────────────────────────────────────────────────────
  function closeQuickAdd() {
    drawer.classList.remove('active');
    overlay.classList.remove('active');
    document.body.style.overflow = '';
    window._bykPickerCallback = null; // clear BYK mode if drawer closed without confirming
  }

  if (closeBtn) closeBtn.addEventListener('click', closeQuickAdd);
  overlay.addEventListener('click', closeQuickAdd);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('active')) closeQuickAdd();
  });

  // ── FORM INIT ─────────────────────────────────────────────────────────────
  function initQuickAddForm(handle) {
    const form = document.getElementById('q-add-form');
    if (!form) return;

    const noVariants = form.dataset.noVariants === 'true';

    // Selector groups (each may or may not be in the DOM)
    const sizeGroup      = document.getElementById('qSizeGroup');
    const sizeSelect     = document.getElementById('qSizeSelect');
    const sizeError      = document.getElementById('qSizeError');
    const braceletGroup  = document.getElementById('qBraceletGroup');
    const braceletSelect = document.getElementById('qBraceletSelect');
    const braceletError  = document.getElementById('qBraceletError');
    const chainGroup     = document.getElementById('qChainGroup');
    const chainSelect    = document.getElementById('qChainSelect');
    const chainError     = document.getElementById('qChainError');
    const stoneGroup     = document.getElementById('qStoneGroup');
    const stoneSelect    = document.getElementById('qStoneSelect');
    const stoneError     = document.getElementById('qStoneError');
    const charmGroup     = document.getElementById('qCharmGroup');
    const charmInput     = document.getElementById('qCharmInput');
    const charmError     = document.getElementById('qCharmError');
    const engravingGroup = document.getElementById('qEngravingGroup');
    const engravingInput = document.getElementById('qEngravingInput');
    const engravingError = document.getElementById('qEngravingError');

    const wishBtn        = document.getElementById('qWishlistBtn');
    const wishLabel      = wishBtn ? wishBtn.querySelector('.wish-label') : null;
    const variantData    = JSON.parse(document.getElementById('qVariantData')?.textContent || '[]');
    const variantIdInput = document.getElementById('qAddVariantId');
    const optionSelects  = form.querySelectorAll('.variant-option-select');
    const priceEl        = document.getElementById('qAddPrice');

    // ── Swatch button handlers — update hidden select + trigger updateVariant ──

    // Metal swatches
    const goldBtns     = document.getElementById('qGoldBtns');
    const goldSelect   = document.getElementById('qGoldVariantSelect');
    if (goldBtns && goldSelect) {
      goldBtns.addEventListener('click', (e) => {
        const btn = e.target.closest('.gold-btn');
        if (!btn) return;
        goldBtns.querySelectorAll('.gold-btn').forEach(b => {
          b.classList.remove('active'); b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('active'); btn.setAttribute('aria-pressed', 'true');
        goldSelect.value = btn.dataset.gold;
        goldSelect.dispatchEvent(new Event('change'));
      });
    }

    // Colourstone swatches
    const colourstoneBtns   = document.getElementById('qColourstoneBtns');
    const colourstoneSelect = document.getElementById('qColourstoneVariantSelect');
    if (colourstoneBtns && colourstoneSelect) {
      colourstoneBtns.addEventListener('click', (e) => {
        const btn = e.target.closest('.stone-btn');
        if (!btn) return;
        colourstoneBtns.querySelectorAll('.stone-btn').forEach(b => {
          b.classList.remove('active'); b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('active'); btn.setAttribute('aria-pressed', 'true');
        colourstoneSelect.value = btn.dataset.colour;
        colourstoneSelect.dispatchEvent(new Event('change'));
      });
    }

    // Closure buttons
    const closureBtns   = document.getElementById('qClosureBtns');
    const closureSelect = document.getElementById('qClosureVariantSelect');
    if (closureBtns && closureSelect) {
      closureBtns.addEventListener('click', (e) => {
        const btn = e.target.closest('.closure-btn');
        if (!btn) return;
        closureBtns.querySelectorAll('.closure-btn').forEach(b => {
          b.classList.remove('active'); b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('active'); btn.setAttribute('aria-pressed', 'true');
        closureSelect.value = btn.dataset.closure;
        closureSelect.dispatchEvent(new Event('change'));
      });
    }

    // Post Length accordion toggle
    const postLengthAccBtn  = document.getElementById('qPostLengthAccBtn');
    const postLengthAccBody = document.getElementById('qPostLengthAccBody');
    if (postLengthAccBtn && postLengthAccBody) {
      postLengthAccBtn.addEventListener('click', () => {
        const open = postLengthAccBody.classList.toggle('open');
        postLengthAccBtn.setAttribute('aria-expanded', String(open));
      });
    }

    // Post Length buttons
    const postLengthBtns   = document.getElementById('qPostLengthBtns');
    const postLengthSelect = document.getElementById('qPostLengthVariantSelect');
    if (postLengthBtns && postLengthSelect) {
      postLengthBtns.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-postlength]');
        if (!btn) return;
        postLengthBtns.querySelectorAll('[data-postlength]').forEach(b => {
          b.classList.remove('active'); b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('active'); btn.setAttribute('aria-pressed', 'true');
        postLengthSelect.value = btn.dataset.postlength;
        postLengthSelect.dispatchEvent(new Event('change'));
      });
    }

    // Helper: attach clear-error listener to a select/input
    function clearOnChange(el, group, errEl) {
      if (!el) return;
      el.addEventListener(el.tagName === 'INPUT' ? 'input' : 'change', () => {
        if (errEl) errEl.classList.remove('visible');
        if (group) group.classList.remove('has-error');
      });
    }
    clearOnChange(sizeSelect,     sizeGroup,     sizeError);
    clearOnChange(braceletSelect, braceletGroup, braceletError);
    clearOnChange(chainSelect,    chainGroup,    chainError);
    clearOnChange(stoneSelect,    stoneGroup,    stoneError);
    clearOnChange(charmInput,     charmGroup,    charmError);
    clearOnChange(engravingInput, engravingGroup, engravingError);

    // Variant switching — set-contains: every selected option value must appear
    // somewhere in the variant's options array. Order-independent.
    function updateVariant() {
      const vals = Array.from(optionSelects).map(s => s.value).filter(Boolean);
      const matched = variantData.find(v => vals.every(sv => v.options.includes(sv)));

      if (matched) {
        variantIdInput.value = matched.id;
        if (priceEl) priceEl.textContent = formatMoney(matched.price);
        const addBtn = form.querySelector('[type="submit"]');
        if (addBtn) addBtn.disabled = !matched.available;
        if (addBtn) addBtn.textContent = matched.available
          ? (typeof window._bykPickerCallback === 'function' && document.getElementById('bykBoxItems') ? 'Add to box' : 'Add to order')
          : 'Sold out';
      } else {
        variantIdInput.value = '';
      }
    }
    optionSelects.forEach(s => s.addEventListener('change', updateVariant));

    // Run once on drawer open so variant ID reflects the pre-selected state
    // (goldInput starts empty until user clicks a swatch, so this also handles
    // products where the server pre-fill is the correct default)
    updateVariant();

    // Wishlist ─────────────────────────────────────────────────────────────
    if (wishBtn) {
      let list = [];
      try { list = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]'); } catch (_) {}
      if (list.includes(handle)) {
        wishBtn.classList.add('saved');
        if (wishLabel) wishLabel.textContent = 'Saved in pantry';
      }
      wishBtn.addEventListener('click', () => {
        let cur = [];
        try { cur = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]'); } catch (_) {}
        const idx = cur.indexOf(handle);
        if (idx > -1) {
          cur.splice(idx, 1);
          wishBtn.classList.remove('saved');
          if (wishLabel) wishLabel.textContent = 'Save for later';
        } else {
          cur.push(handle);
          wishBtn.classList.add('saved');
          if (wishLabel) wishLabel.textContent = 'Saved in pantry';
        }
        try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(cur)); } catch (_) {}
      });
    }

    // Helper: show error on a group, return true (for hasError chaining)
    function flagError(group, errEl, isFirst) {
      if (errEl) errEl.classList.add('visible');
      if (group) {
        group.classList.add('has-error');
        if (isFirst) group.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return true;
    }

    // Submit ───────────────────────────────────────────────────────────────
    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      // No-variant products skip all validation — straight to cart
      if (!noVariants) {
        let hasError = false;

        // Ring size
        if (sizeSelect && !sizeSelect.value)         hasError = flagError(sizeGroup,     sizeError,     !hasError);
        // Bracelet size
        if (braceletSelect && !braceletSelect.value) hasError = flagError(braceletGroup, braceletError, !hasError);
        // Chain length
        if (chainSelect && !chainSelect.value)       hasError = flagError(chainGroup,    chainError,    !hasError);
        // Stone (tag-based property selector)
        if (stoneSelect && !stoneSelect.value)       hasError = flagError(stoneGroup,    stoneError,    !hasError);
        // Charm
        if (charmInput) {
          const val = charmInput.value.trim();
          if (!val || !/^[A-Za-z0-9]$/.test(val)) hasError = flagError(charmGroup, charmError, !hasError);
        }
        // Personalisation
        if (engravingInput && !engravingInput.value.trim()) hasError = flagError(engravingGroup, engravingError, !hasError);

        if (hasError) return;
      }

      // ── BYK MODE: hand off to BYK box instead of adding to cart ─
      if (typeof window._bykPickerCallback === 'function' && document.getElementById('bykBoxItems')) {
        const _bykProps = {};
        if (sizeSelect     && sizeSelect.value)           _bykProps['Ring Size']       = sizeSelect.value;
        if (braceletSelect && braceletSelect.value)       _bykProps['Bracelet Size']   = braceletSelect.value;
        if (chainSelect    && chainSelect.value)          _bykProps['Length']          = chainSelect.value;
        if (stoneSelect    && stoneSelect.value)          _bykProps['Stone']           = stoneSelect.value;
        if (charmInput     && charmInput.value.trim())    _bykProps['Charm']           = charmInput.value.trim();
        if (engravingInput && engravingInput.value.trim()) _bykProps['Personalisation'] = engravingInput.value.trim();
        if (postLengthSelect && postLengthSelect.value)   _bykProps['Post Length']     = postLengthSelect.value;

        // Variant match: all values from optionSelects (metal, colourstone, closure all included)
        const _optVals = Array.from(optionSelects).map(s => s.value).filter(Boolean);
        let _bykVar = variantData.find(v => _optVals.every(sv => v.options.includes(sv)));

        // Fallback: pre-filled input value, then first variant
        if (!_bykVar) {
          const _fallbackId = variantIdInput ? variantIdInput.value : '';
          _bykVar = variantData.find(v => String(v.id) === String(_fallbackId)) || variantData[0];
        }

        const _bykPrice    = _bykVar ? _bykVar.price : 0;
        const _bykActualId = _bykVar ? String(_bykVar.id) : '';
        window._bykPickerCallback({ variantId: _bykActualId, price: _bykPrice, properties: _bykProps });
        window._bykPickerCallback = null;
        closeQuickAdd();
        return;
      }
      // ─────────────────────────────────────────────────────────────

      const submitBtn = form.querySelector('[type="submit"]');
      submitBtn.disabled = true;
      submitBtn.textContent = 'Adding…';

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          body: new FormData(form)
        });
        if (!res.ok) throw new Error('Cart error');

        const data = await res.json();

        showCartToast(data);
        updateCartCount();

        // ── META PIXEL: AddToCart ──────────────────────────────
        if (typeof fbq !== 'undefined') {
          fbq('track', 'AddToCart', {
            content_ids:  [String(data.variant_id || data.id)],
            content_type: 'product',
            content_name: data.product_title || data.title,
            value:        (data.final_price || data.price) / 100,
            currency:     'INR'
          });
        }

        submitBtn.textContent = 'Added ✓';
        setTimeout(() => {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Add to order';
          closeQuickAdd();
        }, 1400);

      } catch (err) {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Error — Try Again';
        console.error('[QuickAdd Cart]', err);
      }
    });
  }

  // ── CART TOAST ────────────────────────────────────────────────────────────
  function showCartToast(item) {
    if (!toast) return;

    const nameEl    = toast.querySelector('.cart-toast-name');
    const variantEl = toast.querySelector('.cart-toast-variant');
    const priceEl   = toast.querySelector('.cart-toast-price');

    if (nameEl)    nameEl.textContent    = item.product_title || item.title;
    if (variantEl) variantEl.textContent = buildVariantLabel(item);
    if (priceEl)   priceEl.textContent   = formatMoney(item.final_price);

    toast.classList.add('visible');

    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => toast.classList.remove('visible'), 3000);
  }

  function buildVariantLabel(item) {
    if (item.variant_title && item.variant_title !== 'Default Title') {
      return item.variant_title;
    }
    return '';
  }

  function formatMoney(cents) {
    return '₹' + (cents / 100).toLocaleString('en-IN');
  }

  // ── CART COUNT ────────────────────────────────────────────────────────────
  async function updateCartCount() {
    try {
      const res  = await fetch('/cart.js');
      const cart = await res.json();
      const badges = document.querySelectorAll('.cart-count');
      badges.forEach(b => {
        b.textContent = cart.item_count;
        b.style.display = cart.item_count > 0 ? '' : 'none';
      });
    } catch (_) {}
  }

  // ── SPINNER KEYFRAME (injected once) ─────────────────────────────────────
  if (!document.getElementById('qa-spin-style')) {
    const s = document.createElement('style');
    s.id = 'qa-spin-style';
    s.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
    document.head.appendChild(s);
  }

})();
